import React, {useState} from "react";
import {observer} from "mobx-react-lite";
import {Modal, ModalBody, ModalFooter} from "reactstrap";
import ButtonIcon from "../../atoms/button/buttonIcon";
import {useTranslation} from "react-i18next";
import "./eventPublishModal.sass";
import ModalDetailHeader from "../../molecules/modalDetailHeader/modalDetailHeader";
import {format} from "date-fns";
import {utcToZonedTime} from "date-fns-tz";
import {useStores} from "../../../stores/useStores";
import parse from "html-react-parser";
import DateEdit from "../dateEdit/dateEdit";
import Flex from "../../atoms/common/flex";
import classNames from "classnames";
import {DateObservable} from "../../../stores/observables/dateObservable";
import {EventHelper} from "../../../shared/eventHelper";

export interface IEventPublishModalProps {
  isOpen: boolean;
  toggle: () => void;
  onConfirmBtnClicked: (effectiveFromDate: Date) => void;
}

const EventPublishModal: React.FC<IEventPublishModalProps> = ({
  isOpen,
  toggle,
  onConfirmBtnClicked
}) => {
  const {t} = useTranslation(["translation", "event"]);
  const [effectiveFromDate] = useState(() => new DateObservable());
  const {
    uiStore: {modalStore, eventsUiStore},
    domainStore: {eventsDomainStore}
  } = useStores();

  if (
    !isOpen ||
    !eventsDomainStore.editableEvent.eventDateDetails.startDate.date
  ) {
    return <></>;
  }
  const message = t("event:messages.publishChangesToSeries", {
    dayOfWeek: format(
      utcToZonedTime(
        eventsDomainStore.editableEvent.eventDateDetails.startDate.date!,
        eventsUiStore.getTimeZone()
      ),
      "iiii"
    ),
    time: format(
      utcToZonedTime(
        eventsDomainStore.editableEvent.eventDateDetails.startDate.date!,
        eventsUiStore.getTimeZone()
      ),
      "h:mmaaa"
    ).replace(":00", "")
  });

  let filteredEventOccurrenceDates = [
    ...eventsDomainStore.eventOccurrenceInfos.map(
      (value) => value.occurrenceDate
    )
  ];

  if (eventsDomainStore.editableEvent.eventDateDetails.isEventInPast()) {
    // Exclude previous occurrences when a user is modifying an event series where the initial occurrence
    // has already taken place in the past.
    const currentDateTime = eventsDomainStore.getCurrentTime();
    filteredEventOccurrenceDates = filteredEventOccurrenceDates.filter(
      (value) => value > currentDateTime
    );
  }

  if (
    eventsDomainStore.editableEvent.hasNonCosmeticChanges &&
    eventsDomainStore.editableEvent.isReleased
  ) {
    filteredEventOccurrenceDates = filteredEventOccurrenceDates.filter(
      (value) => value > eventsDomainStore.getNextReleasePeriodStartDate()!
    );
    // Filter out the occurrences that don't fall within the selected week for a monthly recurring event.
    if (
      eventsDomainStore.editableEvent.eventRecurrenceDetails.monthWeeks.hasChange()
    ) {
      const weeks =
        eventsDomainStore.editableEvent.eventRecurrenceDetails.monthWeeks.getSelectedWeekNumbers();
      const timezone = eventsUiStore.getTimeZone();
      filteredEventOccurrenceDates = EventHelper.filterOccurrences(
        filteredEventOccurrenceDates,
        weeks,
        timezone
      );
    }

    if (filteredEventOccurrenceDates.length <= 0) {
      return <></>;
    }
  }

  if (filteredEventOccurrenceDates && !effectiveFromDate.date) {
    const firstOccurrenceDate = filteredEventOccurrenceDates[0];

    effectiveFromDate.date = firstOccurrenceDate;
    effectiveFromDate.minDate = firstOccurrenceDate;
  }

  const recurrenceEndDate =
    eventsDomainStore.editableEvent.eventRecurrenceDetails.recurrenceEndDate
      .date;

  // A staff member can reduce the end date of an event series. In this case,
  // we only want to show future occurrences up to the new end date.
  filteredEventOccurrenceDates = filteredEventOccurrenceDates.filter(
    (d) => recurrenceEndDate === null || d < recurrenceEndDate
  );

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      modalClassName="event-detail-modal"
      centered
    >
      <ModalDetailHeader isLoading={false} toggle={toggle}>
        <div className="header-container">
          <div className="header-column column-dynamic">
            {t("translation:titles.publishChanges")}
          </div>
        </div>
      </ModalDetailHeader>
      <ModalBody>
        <>
          <div className="cancel-postpone-message">{parse(message)}</div>
          <div className="date-picker-container">
            <DateEdit
              enabledDates={filteredEventOccurrenceDates}
              value={effectiveFromDate}
              label={t("event:labels.effectiveFrom")}
              dateFormat={"dd/MM/yyyy"}
              placeholder={t("event:placeholders.effectiveFromDate")}
              overrideIsRequired={true}
              showTimeSelect={false}
              preventManualEntry={true}
              openToDate={effectiveFromDate.date}
              timeZone={eventsDomainStore.getTimeZone()}
            />
          </div>
        </>
      </ModalBody>
      <ModalFooter
        tag={Flex}
        justify={modalStore.footerButtonAlignment}
        className={classNames("event-publish-modal-footer")}
      >
        <div className="event-publish-modal-buttons">
          <ButtonIcon type="secondary" onClick={toggle}>
            {t("translation:actions.cancel")}
          </ButtonIcon>
        </div>
        <ButtonIcon
          type={"primary-negative"}
          onClick={() => onConfirmBtnClicked(effectiveFromDate.date!)}
          isDisabled={!effectiveFromDate.date}
        >
          {t("translation:confirmations.confirmAndPublish")}
        </ButtonIcon>
      </ModalFooter>
    </Modal>
  );
};

export default observer(EventPublishModal);
